import React, { useRef } from "react";
import PropTypes from "prop-types";
import createMarkup from "create-markup";
import { graphql } from "gatsby";
import { get, isNil } from "lodash/fp";
import { forms } from "../../i18n/forms";

// hoc
import withI18next from "../../i18n/withI18next";

// components
import Layout from "../../components/layout";
import BlueDivide from "../../components/BlueDivide/BlueDivide";
import CaseBody from "../../components/CaseBody/CaseBody";
import CaseModalVideo from "../../components/CaseModalVideo/CaseModalVideo";
import CaseMobileWebsiteView from "../../components/CaseMobileWebsiteView/CaseMobileWebsiteView";
import CompanyRepresentationBlock from "../../components/CompanyRepresentationBlock/CompanyRepresentationBlock";
import BackgroundWithLines from "../../components/Background/BackgroundWithLines";
import SectionWhiteFirst from "../../components/PageSections/SectionWhiteFirst";
import SectionWhiteSecond from "../../components/PageSections/SectionWhiteSecond";
import SectionBlueFirst from "../../components/PageSections/SectionBlueFirst";
import ContactForm from "../../components/Forms/ContactForm";
import { Image } from "../../components/Image/Image";
import { Script } from "../../components/Script/Script";
import LocalizedLink from "../../components/LocalizedLink/LocalizedLink";
import Conditional from "../../components/Conditional/Conditional";

// helpers
import { languageSelectorLocalization } from "../../helpers/localeHelpers";

// styles
import "./case-page.scss";

const CasePage = ({ data, pageContext, location }) => {
  const layoutRef = useRef();

  // constructor(props) {
  //   super(props);
  //   layoutRef = React.createRef();
  // }
  const updateLogoColor = (color) => {
    layoutRef.current.handleLogoColor(color);
  };

  const { contentfulCasePage, contentfulMicrocopyPages, localizations } = data;
  const { locale } = pageContext;
  const {
    subTitle,
    description,
    usps,
    uspImages,
    uspImagesDescription,
    caseVideo,
    title,
    caseWebsiteView,
    heroImage,
    mobileWebsiteView,
    nextCase,
    caseQuote,
    companyRepresentationBlock,
    caseVideoSubTitle,
    linkToCaseWebPage,
    contactPerson,
    metaTitle,
    metaDescription,
    keywords,
    ogImage,
    script,
    awardImage,
  } = contentfulCasePage;

  const { titleInspiration, subtitleInspiration } = contentfulMicrocopyPages;

  const languageSelectorInformation = languageSelectorLocalization(
    "/vara-case",
    localizations
  );

  return (
    <Layout
      title={metaTitle}
      description={metaDescription}
      keywords={keywords}
      ogImage={ogImage && ogImage.file.url}
      location={location}
      ref={layoutRef}
      languageSelectorInformation={languageSelectorInformation}
    >
      <div className="case-page">
        <CaseModalVideo
          backgroundImage={heroImage.fluid}
          title={title}
          slogan={caseVideoSubTitle}
          video={caseVideo !== null ? caseVideo.file.url : ""}
          awardImage={awardImage}
        />
        <BackgroundWithLines theme={"white"}>
          <SectionWhiteFirst logoColor={updateLogoColor} />
          <CaseBody>
            <div className="case-page__container --half">
              <h2 className="case-page__case-sub-title">{subTitle}</h2>
              <p className="case-page__case-description">
                {description.description}
              </p>
              <a
                className="case-page__case-website-link"
                href={linkToCaseWebPage.url}
                target="__blank"
              >
                {linkToCaseWebPage.titel}
              </a>
            </div>
            <div className="case-page__container --third--01">
              {usps &&
                usps.map((usp) => {
                  return (
                    <div className="case-page__usp" key={usp.title}>
                      <span className="case-page__case-usp-numbers">
                        {usp.title}
                      </span>
                      <span className="case-page__case-usp-description">
                        {usp.description}
                      </span>
                    </div>
                  );
                })}
            </div>
            <div className="case-page__container --margin-b-0">
              <div className="case-page__usp-images">
                {uspImages &&
                  uspImages.map((image, index) => (
                    <div
                      className={`case-page__usp-image-${index}`}
                      key={index}
                    >
                      <Image fluid={image.fluid} />
                    </div>
                  ))}

                <span className="case-page__usp-images-description">
                  {uspImagesDescription}
                </span>
              </div>
            </div>
          </CaseBody>
        </BackgroundWithLines>
        <SectionBlueFirst logoColor={updateLogoColor} />
        {caseQuote && (
          <BlueDivide>
            <blockquote
              className="case-page__quote"
              dangerouslySetInnerHTML={createMarkup(
                caseQuote.quoteText.quoteText
              )}
            />
            <span className="case-page__quote-meta">{caseQuote.author}</span>
            <span className="case-page__quote-meta">
              {caseQuote.titleAndCompany ? caseQuote.titleAndCompany : ""}
            </span>
          </BlueDivide>
        )}
        <BackgroundWithLines theme={"white"}>
          <SectionWhiteSecond logoColor={updateLogoColor} />
          {caseWebsiteView && (
            <CaseBody>
              <div className="case-page__container --third--01">
                <div className="case-page__website-container">
                  <h2 className="case-page__website-view-title">
                    {caseWebsiteView.title}
                  </h2>
                  <p className="case-page__website-view-description">
                    {caseWebsiteView.description.description}
                  </p>
                  <ul>
                    {caseWebsiteView.usps.map((usp) => (
                      <li
                        className="case-page__website-view-list-item"
                        key={usp}
                      >
                        {usp}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="case-page__container --half--01">
                <Image
                  className="case-page__website-view-image"
                  fluid={caseWebsiteView.websiteImage.fluid}
                />
              </div>
            </CaseBody>
          )}

          {mobileWebsiteView && (
            <CaseMobileWebsiteView
              backgroundImage={mobileWebsiteView.backgroundImage.fluid}
              firstImage={mobileWebsiteView.firstMobileView.file.url}
              firstVideo={mobileWebsiteView.firstMobileViewVideo.file.url}
              firstVideoMobile={
                mobileWebsiteView.firstMobileViewVideoMobile.file.url
              }
              secondImage={mobileWebsiteView.secondMobileView.file.url}
              secondVideo={mobileWebsiteView.secondMobileViewVideo.file.url}
              secondVideoMobile={
                mobileWebsiteView.secondMobileViewVideoMobile.file.url
              }
            />
          )}
          {companyRepresentationBlock !== null && (
            <CompanyRepresentationBlock
              video={
                companyRepresentationBlock.video &&
                companyRepresentationBlock.video.file.url
              }
              backgroundImage={companyRepresentationBlock.backgroundImage.fluid}
              quote={companyRepresentationBlock.quote.quote}
              name={companyRepresentationBlock.name}
              position={companyRepresentationBlock.position}
            />
          )}
          {nextCase && (
            <div className="case-page__next-case">
              <div className="case-page__next-case-title-container">
                <h2 className="case-page__next-case-title">
                  {titleInspiration}{" "}
                  <span className="italic">{subtitleInspiration}</span>
                </h2>
              </div>
              <div key={nextCase.id} className="case-page__case-container">
                <div className="case-page__case-link-container">
                  <LocalizedLink
                    to={nextCase.slug}
                    className="case-page__case-link"
                  >
                    <Conditional show={!isNil(get("awardImage", nextCase))}>
                      <img
                        className="case-page__next-case-award-image"
                        src={get(["awardImage", "file", "url"], nextCase)}
                        alt=""
                      />
                    </Conditional>
                    <div className="case-page__case-image-container">
                      <Image
                        className="case-page__case-image"
                        fluid={nextCase.heroImage.fluid}
                      />
                      <h2 className="case-page__overlay-title">
                        {nextCase.title}
                      </h2>
                    </div>
                  </LocalizedLink>
                </div>
              </div>
            </div>
          )}
        </BackgroundWithLines>
        <ContactForm
          iframe={forms[locale].contact}
          contactPerson={contactPerson.image}
          label={contactPerson.label}
        />
      </div>
      {script && <Script script={script.script} />}
    </Layout>
  );
};

export default withI18next()(CasePage);

CasePage.defaultProps = {
  data: {
    contentfulCase: {
      caseQuote: {},
      usps: [
        {
          title: "",
          description: "",
        },
      ],
      uspImages: [
        {
          file: {
            url: "",
          },
        },
      ],
      uspImagesDescription: "",
      caseVideoSubTitle: "",
      description: {
        description: "",
      },
      webPageLink: "",
      subTitle: "",
    },
  },
};

CasePage.propTypes = {
  data: PropTypes.shape({
    contentfulCase: PropTypes.shape({
      subTitle: PropTypes.string,
      description: PropTypes.object,
      usps: PropTypes.array,
      uspImages: PropTypes.array,
      webPageLink: PropTypes.string,
      uspImagesDescription: PropTypes.string,
      caseVideo: PropTypes.object,
      caseVideoSubTitle: PropTypes.string,
      title: PropTypes.string,
      caseWebsiteView: PropTypes.object,
      heroImage: PropTypes.object,
      mobileWebsiteView: PropTypes.object,
      nextCase: PropTypes.object,
      companyRepresentationBlock: PropTypes.object,
      caseQuote: PropTypes.object,
      awardImage: PropTypes.object,
    }),
  }),
};

export const pageQuery = graphql`
  query casePageQuery($trueSlug: String!, $locale: String!) {
    contentfulCasePage(slug: { eq: $trueSlug }, language: { eq: $locale }) {
      title
      metaTitle
      metaDescription
      keywords
      ogImage {
        file {
          url
        }
      }
      companyRepresentationBlock {
        backgroundImage {
          fluid(quality: 100) {
            ...GatsbyContentfulFluid
          }
        }
        video {
          file {
            url
          }
        }
        quote {
          quote
        }
        name
        position
      }
      nextCase {
        title
        slug
        shortDescription
        awardImage {
          file {
            url
          }
        }
        heroImage {
          fluid(quality: 75) {
            ...GatsbyContentfulFluid
          }
          file {
            url
          }
        }
      }
      caseVideo {
        file {
          url
        }
      }
      caseVideoSubTitle
      mobileWebsiteView {
        backgroundImage {
          fluid(quality: 100) {
            ...GatsbyContentfulFluid
          }
        }
        firstMobileView {
          file {
            url
          }
        }
        firstMobileViewVideo {
          file {
            url
          }
        }
        firstMobileViewVideoMobile {
          file {
            url
          }
        }
        secondMobileView {
          file {
            url
          }
        }
        secondMobileViewVideo {
          file {
            url
          }
        }
        secondMobileViewVideoMobile {
          file {
            url
          }
        }
      }
      caseWebsiteView {
        id
        title
        description {
          description
        }
        usps
        websiteImage {
          id
          fluid(quality: 100, maxWidth: 621) {
            ...GatsbyContentfulFluid
          }
        }
      }
      subTitle
      description {
        description
      }
      linkToCaseWebPage {
        titel
        url
      }
      usps {
        title
        description
      }
      uspImages {
        fluid(quality: 75) {
          ...GatsbyContentfulFluid
        }
        file {
          url
        }
      }
      uspImagesDescription
      caseQuote {
        id
        quoteText {
          id
          quoteText
        }
        author
        titleAndCompany
      }
      heroImage {
        fluid(quality: 100, maxWidth: 2900) {
          ...GatsbyContentfulFluid
        }
      }
      script {
        script
      }
      contactPerson {
        label
        image {
          file {
            url
          }
        }
      }
      awardImage {
        file {
          url
        }
      }
    }
    localizations: allContentfulCasePage(filter: { slug: { eq: $trueSlug } }) {
      edges {
        node {
          slug
          language
        }
      }
    }
    contentfulMicrocopyPages(language: { eq: $locale }) {
      titleInspiration
      subtitleInspiration
    }
  }
`;